/* Made in Camelot game finder. Built from finder-score.js + game-finder-ui.js. */
/*
 * finder-score.js — Made in Camelot board game finder: matching logic
 *
 * Plain JS with no Node APIs, so the same file runs in the GitHub Action
 * (preview report) and later in the storefront finder page.
 *
 * Game shape (from finder-index.json):
 *   { bgg, name, year, expansion, players:{min,max,best:[],rec:[]},
 *     time:{min,max}, weight, age, rank, mech:[], cat:[], fam:[], shop:[...] }
 */
(function (root) {
  'use strict';

  // How much each tag group counts towards "feels like" similarity
  const GROUP_WEIGHT = { m: 1.0, c: 0.8, f: 0.6 };

  // Tags decide the match; weight and playtime only adjust it (0.6–1.0 multiplier),
  // so a game that shares nothing scores 0 instead of ranking on weight/time alone
  const BLEND = { base: 0.6, weight: 0.2, time: 0.2 };

  // Below this tag similarity a game is not a match at all
  const MIN_TAG_SCORE = 0.08;

  // "Like Patchwork" should never suggest Arkwright: skip games whose BGG weight differs by more than this
  const MAX_WEIGHT_GAP = 1.6;

  // Tags that describe player count or product type rather than how a game feels
  const IGNORE = {
    m2819: true,  // Solo / Solitaire Game (handled by the player filter)
    c1042: true   // Expansion for Base-game
  };

  function tagKeys(g) {
    const keys = [];
    (g.mech || []).forEach(id => keys.push('m' + id));
    (g.cat || []).forEach(id => keys.push('c' + id));
    (g.fam || []).forEach(id => keys.push('f' + id));
    return keys.filter(k => !IGNORE[k]);
  }

  // Rarer tags count for more: "Dice Rolling" is everywhere, "Network Building" is a signal
  function buildIdf(games) {
    const df = {};
    let n = 0;
    games.forEach(g => {
      if (g.expansion) return;
      n++;
      new Set(tagKeys(g)).forEach(k => { df[k] = (df[k] || 0) + 1; });
    });
    const idf = {};
    Object.keys(df).forEach(k => { idf[k] = Math.log(1 + n / df[k]); });
    return idf;
  }

  function tagWeight(k, idf) {
    return (idf[k] || 0) * GROUP_WEIGHT[k[0]];
  }

  // Weighted Ochiai (cosine on weighted binary tags): doesn't punish games with many tags as hard as Jaccard
  function tagSimilarity(a, b, idf) {
    const ka = new Set(tagKeys(a));
    const kb = new Set(tagKeys(b));
    let sumA = 0, sumB = 0, shared = 0;
    const sharedKeys = [];
    ka.forEach(k => { sumA += tagWeight(k, idf); });
    kb.forEach(k => {
      const w = tagWeight(k, idf);
      sumB += w;
      if (ka.has(k)) { shared += w; sharedKeys.push(k); }
    });
    const score = sumA && sumB ? shared / Math.sqrt(sumA * sumB) : 0;
    sharedKeys.sort((x, y) => tagWeight(y, idf) - tagWeight(x, idf));
    return { score, sharedKeys };
  }

  function weightCloseness(a, b) {
    if (!a.weight || !b.weight) return 0.5;
    return 1 - Math.min(Math.abs(a.weight - b.weight) / 1.5, 1);
  }

  function avgTime(g) {
    if (!g.time) return 0;
    const lo = g.time.min || g.time.max || 0;
    const hi = g.time.max || g.time.min || 0;
    return (lo + hi) / 2;
  }

  function timeCloseness(a, b) {
    const ta = avgTime(a), tb = avgTime(b);
    if (!ta || !tb) return 0.5;
    return 1 - Math.min(Math.abs(Math.log(ta / tb)) / Math.log(4), 1);
  }

  function similarity(a, b, idf) {
    const t = tagSimilarity(a, b, idf);
    const w = weightCloseness(a, b);
    const tm = timeCloseness(a, b);
    return {
      score: t.score * (BLEND.base + BLEND.weight * w + BLEND.time * tm),
      parts: { tags: t.score, weight: w, time: tm },
      sharedKeys: t.sharedKeys
    };
  }

  // Player count fit: uses the BGG community poll ("rec"), falls back to box min/max
  function fitsPlayers(g, n) {
    if (!g.players) return false;
    if (g.players.rec && g.players.rec.length) return g.players.rec.indexOf(n) !== -1;
    return n >= (g.players.min || 0) && n <= (g.players.max || 0);
  }

  function isBestAt(g, n) {
    return !!(g.players && g.players.best && g.players.best.indexOf(n) !== -1);
  }

  // "Games like X" — only shop games, never expansions, never X itself
  function likeGame(target, games, idf, opts) {
    opts = opts || {};
    const limit = opts.limit || 8;
    return games
      .filter(g => g.shop && g.shop.length && !g.expansion && g.bgg !== target.bgg)
      .filter(g => !opts.players || fitsPlayers(g, opts.players))
      .filter(g => !opts.inStockOnly || g.shop.some(p => p.available))
      .map(g => Object.assign({ game: g }, similarity(target, g, idf)))
      .filter(r => r.parts.tags >= MIN_TAG_SCORE)
      .filter(r => !target.weight || !r.game.weight || Math.abs(target.weight - r.game.weight) <= MAX_WEIGHT_GAP)
      .sort((x, y) => y.score - x.score)
      .slice(0, limit);
  }

  const api = { buildIdf, tagKeys, tagWeight, similarity, fitsPlayers, isBestAt, likeGame, GROUP_WEIGHT, BLEND, MIN_TAG_SCORE, MAX_WEIGHT_GAP, IGNORE };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.CamelotFinder = api;
})(typeof window !== 'undefined' ? window : this);
/*
 * Made in Camelot game finder: storefront UI.
 * Works with sections/game-finder.liquid and templates/product.finder-card.liquid.
 * Every .gf[data-gf] block on the page runs independently (full page or compact homepage block).
 */
(function () {
  'use strict';
  const F = window.CamelotFinder;
  if (!F) return;

  // ---------- shared data (one fetch per page) ----------
  const indexCache = {};
  function loadIndex(url) {
    if (!indexCache[url]) {
      indexCache[url] = fetch(url, { credentials: 'omit' })
        .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(prepare);
    }
    return indexCache[url];
  }

  function prepare(INDEX) {
    const V = INDEX.vocab;
    const ALL = INDEX.games;
    const SHOP = ALL.filter(g => g.shop && g.shop.length && !g.expansion);
    const IDF = F.buildIdf(ALL);
    const byName = { m: {}, c: {}, f: {} };
    [['m', 'mech'], ['c', 'cat'], ['f', 'fam']].forEach(([p, k]) => {
      Object.entries(V[k]).forEach(([id, name]) => { byName[p][name] = p + id; });
    });
    const keyCache = new Map();
    const keysOf = g => { let s = keyCache.get(g); if (!s) { s = new Set(F.tagKeys(g)); keyCache.set(g, s); } return s; };
    const hasAny = (g, keys) => { const s = keysOf(g); return keys.some(k => s.has(k)); };
    const resolve = chips => chips
      .map(([label, refs]) => ({ label, keys: refs.map(r => byName[r[0]][r.slice(2)]).filter(Boolean) }))
      .filter(c => c.keys.length && SHOP.some(g => hasAny(g, c.keys)));
    const searchable = ALL.filter(g => !g.expansion && g.mech.length)
      .map(g => ({ g, key: norm(g.name), alt: (ALIASES[g.bgg] || []).map(norm), altRaw: ALIASES[g.bgg] || [] }));
    return {
      INDEX, V, ALL, SHOP, IDF, hasAny, searchable,
      MECHS: resolve(MECH_CHIPS), THEMES: resolve(THEME_CHIPS),
      byBgg: new Map(ALL.map(g => [g.bgg, g]))
    };
  }

  // UK and everyday names BGG files under something else, keyed by BGG id
  const ALIASES = {
    1294: ['Cluedo'], 63268: ['Dobble'], 2083: ['Draughts'], 2136: ['Ludo'], 45: ['Perudo'],
    2921: ['Game of Life', 'Life'], 2389: ['Reversi'], 2425: ['Battleships'], 2719: ['Connect 4'],
    2407: ['Sorry'], 4143: ['Guess Who'], 50381: ['CAH'], 244522: ['Ganz schon clever'],
    244521: ['The Quacks of Quedlinburg', 'Quacks of Quedlinburg'], 13: ['Settlers of Catan'],
    1406: ['Monopoly Classic'], 40398: ['Monopoly Deal Card Game'], 2952: ['Trivial'],
    256382: ['Villainous'], 266524: ['Parks']
  };
  function norm(s) { return String(s).toLowerCase().normalize('NFKD').replace(/[^\w\s]/g, '').trim(); }
  function esc(s) { return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]); }

  // ---------- option sets ----------
  const PLAYERS = [
    { id: 'any', label: 'Any' },
    { id: '1', label: 'Just me', n: 1 },
    { id: '2', label: 'Two of us', n: 2 },
    { id: '3', label: '3', n: 3 },
    { id: '4', label: '4', n: 4 },
    { id: '5', label: '5', n: 5 },
    { id: '6', label: '6+', n: 6, plus: true }
  ];
  const TIMES = [
    { id: 'any', label: 'Any' },
    { id: 'q', label: 'Under 30 min', lo: 0, hi: 30 },
    { id: 'm', label: '30–60 min', lo: 30, hi: 60 },
    { id: 'l', label: '1–2 hours', lo: 60, hi: 120 },
    { id: 'xl', label: '2 hours +', lo: 120, hi: 9999 }
  ];
  const WEIGHTS = [
    { id: 'any', label: 'Any' },
    { id: 'light', label: 'Light & easy', lo: 0, hi: 2, bars: 1 },
    { id: 'mid', label: 'Some strategy', lo: 2, hi: 3, bars: 2 },
    { id: 'heavy', label: 'Brain burner', lo: 3, hi: 6, bars: 3 }
  ];
  const MECH_CHIPS = [
    ['Co-operative', ['m:Cooperative Game']],
    ['Deck building', ['m:Deck, Bag, and Pool Building', 'm:Deck Construction']],
    ['Worker placement', ['m:Worker Placement', 'm:Worker Placement, Different Worker Types', 'm:Worker Placement with Dice Workers']],
    ['Tile laying', ['m:Tile Placement']],
    ['Card drafting', ['m:Open Drafting', 'm:Closed Drafting']],
    ['Engine building', ['f:Mechanism: Tableau Building', 'm:Tags']],
    ['Area control', ['m:Area Majority / Influence']],
    ['Route building', ['m:Network and Route Building', 'm:Connections']],
    ['Set collection', ['m:Set Collection']],
    ['Pattern puzzles', ['m:Pattern Building', 'm:Grid Coverage']],
    ['Push your luck', ['m:Push Your Luck']],
    ['Dice', ['m:Dice Rolling', 'c:Dice']],
    ['Deduction', ['m:Deduction', 'c:Deduction']],
    ['Bluffing & hidden roles', ['c:Bluffing', 'm:Hidden Roles']],
    ['Trick-taking', ['m:Trick-taking']],
    ['Campaign / story', ['m:Scenario / Mission / Campaign Game', 'm:Narrative Choice / Paragraph', 'm:Storytelling']],
    ['Roll & write', ['f:Mechanism: Roll-and-Write', 'f:Mechanism: Flip-and-Write', 'm:Paper-and-Pencil']],
    ['Racing', ['m:Race', 'c:Racing']],
    ['Trading & negotiation', ['m:Trading', 'm:Negotiation', 'c:Negotiation']],
    ['Team play', ['m:Team-Based Game']]
  ];
  const THEME_CHIPS = [
    ['Fantasy', ['c:Fantasy']],
    ['Sci-fi & space', ['c:Science Fiction', 'c:Space Exploration']],
    ['Animals', ['c:Animals']],
    ['Nature', ['c:Environmental', 'f:Theme: Nature']],
    ['Horror', ['c:Horror']],
    ['Murder mystery', ['c:Murder / Mystery']],
    ['Medieval', ['c:Medieval']],
    ['Myths & legends', ['c:Mythology']],
    ['Adventure', ['c:Adventure', 'c:Exploration']],
    ['Nautical & pirates', ['c:Nautical', 'c:Pirates']],
    ['Economic', ['c:Economic', 'c:Industry / Manufacturing']],
    ['Party', ['c:Party Game', 'c:Humor']],
    ['Abstract', ['c:Abstract Strategy']],
    ['Puzzle', ['c:Puzzle']],
    ['Food & cooking', ['f:Theme: Food / Cooking']],
    ['Trains', ['c:Trains']]
  ];

  // ---------- reason wording ----------
  const FRIENDLY = {
    'Network and Route Building': 'route building', 'Open Drafting': 'card drafting', 'Closed Drafting': 'card drafting',
    'Deck, Bag, and Pool Building': 'deck building', 'Area Majority / Influence': 'area control', 'Cooperative Game': 'co-operative',
    'Scenario / Mission / Campaign Game': 'campaign play', 'Team-Based Game': 'team play', 'Worker Placement, Different Worker Types': 'worker placement',
    'Dice Rolling': 'dice', 'Mechanism: Tableau Building': 'engine building', 'Tags': 'engine building', 'Connections': 'route building',
    'Narrative Choice / Paragraph': 'story choices', 'Mechanism: Roll-and-Write': 'roll & write', 'Murder / Mystery': 'murder mystery'
  };
  const SKIP = /^(Continents|Country|Cities|States|Region|Islands|Mountains|Rivers|Decades|Promotional|Players): /;
  const LOW = new Set(['End Game Bonuses', 'Variable Set-up', 'Hand Management', 'Card Game', 'Variable Player Powers', 'Turn Order: Progressive', 'Income', 'Multi-Use Cards']);

  // ---------- one finder block ----------
  function Finder(root) {
    const cfg = {
      indexUrl: root.dataset.indexUrl,
      sectionId: root.dataset.sectionId,
      compact: root.dataset.layout === 'compact',
      count: parseInt(root.dataset.count, 10) || 12,
      fullUrl: root.dataset.fullUrl || '',
      showTags: root.dataset.showTags !== 'false',
      showStock: root.dataset.showStock !== 'false',
      defaultPlayers: root.dataset.defaultPlayers || 'any',
      showFit: root.dataset.showFit !== 'false'
    };
    const $ = role => root.querySelector(`[data-role="${role}"]`);
    let D = null;
    const state = { players: cfg.defaultPlayers, time: 'any', weight: 'any', mode: 'like', like: null, tags: new Set(), stock: true };
    let renderToken = 0;

    // --- helpers that need data ---
    const tagLabel = k => D.V[{ m: 'mech', c: 'cat', f: 'fam' }[k[0]]][k.slice(1)] || '';
    function niceTag(k) {
      const raw = tagLabel(k);
      if (!raw || SKIP.test(raw)) return '';
      if (FRIENDLY[raw]) return FRIENDLY[raw];
      const t = raw.replace(/^(Theme|Mechanism|Animals|Creatures|Space|Setting|Occupation|Sports|Mythology|Word Games|Card Games|Folk Tales & Fairy Tales|Books|Characters|Video Game Theme|History|Ancient|Game): /, '');
      // Lowercase ordinary words ('Set Collection' → 'set collection'), keep acronyms like 'RPG'
      return /[A-Z]{2}/.test(t) ? t : t.toLowerCase();
    }
    // How it plays first, then theme, then the generic stuff
    function reasonsFrom(keys) {
      const play = [], theme = [], weak = [];
      keys.forEach(k => {
        const n = niceTag(k); if (!n) return;
        if (LOW.has(tagLabel(k))) weak.push(n);
        else if (k[0] === 'm' || /^Mechanism: /.test(tagLabel(k))) play.push(n);
        else theme.push(n);
      });
      return [...new Set([...play.slice(0, 2), ...theme, ...play.slice(2), ...weak])].slice(0, 3);
    }
    // Same series = a shared BGG "Game:" family, or the same name before a colon ("Elden Ring: …")
    const FRANCHISE = /^(Game|Video Game Theme): /;
    const franchiseKeys = g => {
      const keys = (g.fam || []).filter(id => FRANCHISE.test(D.V.fam[id] || '')).map(id => 'f' + id);
      const i = g.name.indexOf(': ');
      if (i >= 4) keys.push('n' + g.name.slice(0, i).toLowerCase());
      return keys;
    };
    const franchiseName = k => k[0] === 'n'
      ? (D.ALL.find(g => g.name.toLowerCase().startsWith(k.slice(1) + ': ')) || { name: k.slice(1) }).name.split(': ')[0]
      : tagLabel(k).replace(FRANCHISE, '').replace(/ \(Official\)$/, '');
    const avgTime = g => ({ lo: g.time.min || g.time.max || 0, hi: g.time.max || g.time.min || 0 });
    const inStock = g => g.shop.some(p => p.available);
    const popularity = g => g.rank ? Math.max(0, 1 - Math.log10(g.rank) / 4.5) : 0;

    function fitsPlayers(g) {
      const p = PLAYERS.find(x => x.id === state.players);
      if (!p || !p.n) return true;
      if (p.plus) { const rec = g.players.rec || []; return rec.length ? rec.some(n => n >= 6) : (g.players.max || 0) >= 6; }
      return F.fitsPlayers(g, p.n);
    }
    function bestForChoice(g) {
      const p = PLAYERS.find(x => x.id === state.players);
      if (!p || !p.n) return false;
      return p.plus ? (g.players.best || []).some(n => n >= 6) : F.isBestAt(g, p.n);
    }
    function fitsTime(g) {
      const t = TIMES.find(x => x.id === state.time);
      if (!t || t.hi === undefined) return true;
      const { lo, hi } = avgTime(g);
      if (!lo && !hi) return true;
      const mid = (lo + hi) / 2;
      return mid >= t.lo && mid <= t.hi;
    }
    function fitsWeight(g) {
      const w = WEIGHTS.find(x => x.id === state.weight);
      if (!w || w.hi === undefined || !g.weight) return true;
      return g.weight >= w.lo && g.weight < w.hi;
    }
    const basePool = exclude => D.SHOP.filter(g => g.bgg !== exclude && fitsPlayers(g) && fitsTime(g) && fitsWeight(g) && (!state.stock || inStock(g)));

    // --- ranking ---
    function rankLike(target) {
      const out = [];
      for (const g of basePool(target.bgg)) {
        if (state.weight === 'any' && target.weight && g.weight && Math.abs(target.weight - g.weight) > F.MAX_WEIGHT_GAP) continue;
        const s = F.similarity(target, g, D.IDF);
        if (s.parts.tags < F.MIN_TAG_SCORE) continue;
        out.push({ g, score: s.score * (bestForChoice(g) ? 1.08 : 1), reasons: reasonsFrom(s.sharedKeys), conf: s.parts.tags });
      }
      return out.sort((a, b) => b.score - a.score);
    }
    function rankTags() {
      const chips = [...D.MECHS, ...D.THEMES].filter(c => state.tags.has(c.label));
      const wOf = c => Math.max(...c.keys.map(k => D.IDF[k] || 0));
      const total = chips.reduce((s, c) => s + wOf(c), 0);
      const out = [];
      for (const g of basePool(null)) {
        const hit = chips.filter(c => D.hasAny(g, c.keys));
        if (!hit.length) continue;
        const score = hit.reduce((s, c) => s + wOf(c), 0) / total + popularity(g) * 0.08 + (bestForChoice(g) ? 0.05 : 0);
        out.push({ g, score, reasons: hit.map(c => c.label.toLowerCase()), conf: hit.length / chips.length });
      }
      return out.sort((a, b) => b.score - a.score);
    }
    // Solo-only games when "Just me" is picked, two-player-only games when "Two of us" is picked
    function dedicatedTo(g) {
      const p = PLAYERS.find(x => x.id === state.players);
      if (!p || !p.n || p.plus) return false;
      return g.players && g.players.max === p.n && (p.n === 1 || g.players.min === p.n);
    }
    const dedicatedLabel = () => state.players === '1' ? 'Made for solo play' : 'Made for two';
    // Dedicated games first, then everything else that plays at that count; order within each group unchanged
    function dedicatedFirst(results) {
      if (state.players !== '1' && state.players !== '2') return results;
      const first = [], rest = [];
      results.forEach(r => (dedicatedTo(r.g) ? first : rest).push(r));
      return first.concat(rest);
    }

    function rankBrowse() {
      return basePool(null).map(g => {
        const reasons = [];
        if (dedicatedTo(g)) reasons.push(dedicatedLabel());
        if (bestForChoice(g)) reasons.push('Great at this player count');
      //  if (g.rank && g.rank <= 2000) reasons.push('ranked #' + g.rank.toLocaleString('en-GB') + ' on BGG');
        return { g, score: popularity(g) + (bestForChoice(g) ? 0.3 : 0), reasons, conf: 1, browse: true };
      }).sort((a, b) => b.score - a.score);
    }
    function shelve(results, target) {
      const targetFr = new Set(target ? franchiseKeys(target) : []);
      const main = [], family = [], seen = new Map();
      for (const r of results) {
        const fr = franchiseKeys(r.g);
        if (fr.some(k => targetFr.has(k))) { family.push(r); continue; }
        if (fr.some(k => seen.has(k))) continue;
        fr.forEach(k => seen.set(k, r));
        main.push(r);
      }
      return { main, family };
    }

    // --- product cards from the theme ---
    const cardCache = new Map();
    function cardUrl(handle) {
      const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return `${rootPath}products/${encodeURIComponent(handle)}?view=finder-card`;
    }
    function fetchCard(handle) {
      if (!cardCache.has(handle)) {
        cardCache.set(handle, fetch(cardUrl(handle), { headers: { Accept: 'text/html' } })
          .then(r => (r.ok ? r.text() : null))
          .then(html => (html && html.includes('card-spine-wrap') ? html : null))
          .catch(() => null));
      }
      return cardCache.get(handle);
    }
    const pickProduct = g => g.shop.find(p => p.available) || g.shop[0];

    // Fetch cards in rank order; skip any product that no longer renders (unpublished since last night's index)
    async function cardsFor(results, n) {
      const out = [];
      let i = 0;
      while (out.length < n && i < results.length) {
        const batch = results.slice(i, i + (n - out.length) + 2);
        i += batch.length;
        const htmls = await Promise.all(batch.map(r => fetchCard(pickProduct(r.g).handle)));
        batch.forEach((r, j) => { if (htmls[j] && out.length < n) out.push({ r, html: htmls[j] }); });
      }
      return out;
    }

    // "Best at 2 or 3" from BGG's player poll
    function bestText(g) {
      const b = (g.players && g.players.best ? [...g.players.best] : []).sort((x, y) => x - y);
      if (!b.length) return '';
      if (b.length === 1 && b[0] === 1) return 'Best solo';
      if (b.length === 1 && b[0] === 2) return 'Best in 2';
      if (b.length === 1) return `Best at ${b[0]} players`;
      if (b.length === 2 && b[0] === 1 && b[1] === 2) return 'Best solo or in 2';
      const run = b.every((n, i) => !i || n === b[i - 1] + 1);
      if (b.length === 2) return `Best at ${b[0]} or ${b[1]} players`;
      return run ? `Best at ${b[0]}–${b[b.length - 1]} players` : `Best at ${b.slice(0, -1).join(', ')} or ${b[b.length - 1]} players`;
    }
    // Brainpower bar: same three levels as the "How much brainpower?" options (BGG weight 1–5)
    function brainLevel(w) { return w < 2 ? { n: 1, label: 'Light & easy' } : w < 3 ? { n: 2, label: 'Some strategy' } : { n: 3, label: 'Brain burner' }; }
    const barSegs = n => [1, 2, 3].map(i => `<i${i <= n ? ' class="on"' : ''}></i>`).join('');
    function meterHtml(w) {
      if (!w) return '';
      const lvl = brainLevel(w);
      return `<span class="gf-meter" role="img" title="Brainpower: ${lvl.label} (${w.toFixed(1)} of 5 on BoardGameGeek)" aria-label="Brainpower: ${lvl.label}">${barSegs(lvl.n)}</span>`;
    }
    function fitRow(g) {
      if (!cfg.showFit || !g) return '';
      const best = bestText(g), meter = meterHtml(g.weight);
      if (!best && !meter) return '';
      return `<div class="gf-fit"><span class="gf-best">${esc(best)}</span>${meter}</div>`;
    }

    // Adds to the theme's card: the "Because" strip across the top (like the category spine)
    // and the "Best at… / complexity" row just above the price and button
    function itemHtml({ r, html }) {
      let strip = '';
      if (r.reasons && r.reasons.length) {
        // First reason keeps its capital, the rest read on in lowercase
        const txt = r.reasons.map((x, i) => (i && !/^[A-Z]{2}/.test(x) ? x.charAt(0).toLowerCase() + x.slice(1) : x)).join(', ');
        strip = r.browse
          ? `<div class="gf-because"><span>${esc(txt.charAt(0).toUpperCase() + txt.slice(1))}</span></div>`
          : `<div class="gf-because"><span><strong>Because:</strong> ${esc(txt)}</span></div>`;
      }
      const fit = fitRow(r.g);
      if (!strip && !fit) return `<li class="gf-item">${html}</li>`;
      const tpl = document.createElement('template');
      tpl.innerHTML = html.trim();
      const wrap = tpl.content.querySelector('.card-spine-wrap');
      if (!wrap) return `<li class="gf-item">${strip}${html}</li>`;
      if (strip) {
        (tpl.content.querySelector('.card-spine__main') || wrap).insertAdjacentHTML('afterbegin', strip);
        wrap.classList.add('gf-has-because');
      }
      if (fit) {
        const buy = tpl.content.querySelector('.card-spine__buyrow');
        const info = tpl.content.querySelector('.card-spine__info');
        if (buy) buy.insertAdjacentHTML('beforebegin', fit);
        else if (info) info.insertAdjacentHTML('beforeend', fit);
      }
      const out = document.createElement('div');
      out.appendChild(tpl.content);
      return `<li class="gf-item">${out.innerHTML}</li>`;
    }

    function afterInsert(container) {
      // Give the add-to-cart forms this section's id, as the theme's own sections do
      container.querySelectorAll('product-form-component').forEach(el => { el.dataset.sectionId = cfg.sectionId; });
      // Ask Swym to attach to the new wishlist buttons
      try {
        if (window._swat && typeof window._swat.initializeActionButtons === 'function') window._swat.initializeActionButtons('.gf');
        else if (window.SwymCallbacks) window.SwymCallbacks.push(swat => { try { swat.initializeActionButtons('.gf'); } catch (e) {} });
      } catch (e) { /* wishlist is optional */ }
      document.dispatchEvent(new CustomEvent('game-finder:rendered', { detail: { root } }));
    }

    function skeleton(n) {
      return Array.from({ length: n }, () => '<li class="gf-item gf-item--loading" aria-hidden="true"><div class="gf-skel"></div></li>').join('');
    }

    // --- summary text ---
    function filterBits() {
      const bits = [];
      const p = PLAYERS.find(x => x.id === state.players);
      if (p && p.n) bits.push(p.n === 1 ? 'solo' : p.plus ? '6+ players' : p.n === 2 ? 'two players' : `${p.n} players`);
      const t = TIMES.find(x => x.id === state.time); if (t && t.hi !== undefined) bits.push(t.label.toLowerCase());
      const w = WEIGHTS.find(x => x.id === state.weight); if (w && w.hi !== undefined) bits.push(w.label.toLowerCase());
      if (state.stock) bits.push('in stock');
      return bits;
    }

    // --- URL state (shareable links, homepage → full page hand-off) ---
    function toParams() {
      const p = new URLSearchParams();
      if (state.players !== 'any') p.set('players', state.players);
      if (state.mode === 'like' && state.like) p.set('like', state.like.bgg);
      if (state.mode === 'tags' && state.tags.size) { p.set('mode', 'tags'); p.set('tags', [...state.tags].join('|')); }
      if (state.time !== 'any') p.set('time', state.time);
      if (state.weight !== 'any') p.set('weight', state.weight);
      if (!state.stock) p.set('stock', '0');
      return p;
    }
    function fromParams() {
      const p = new URLSearchParams(location.search);
      if (PLAYERS.some(x => x.id === p.get('players'))) state.players = p.get('players');
      if (TIMES.some(x => x.id === p.get('time'))) state.time = p.get('time');
      if (WEIGHTS.some(x => x.id === p.get('weight'))) state.weight = p.get('weight');
      if (p.get('stock') === '0') state.stock = false;
      const like = D.byBgg.get(parseInt(p.get('like'), 10));
      if (like) { state.like = like; state.mode = 'like'; }
      if (p.get('mode') === 'tags' && cfg.showTags) {
        state.mode = 'tags';
        const valid = new Set([...D.MECHS, ...D.THEMES].map(c => c.label));
        (p.get('tags') || '').split('|').filter(t => valid.has(t)).forEach(t => state.tags.add(t));
      }
    }

    // --- render ---
    async function render() {
      const token = ++renderToken;
      let mode = 'browse', results, target = null;
      if (state.mode === 'like' && state.like) { mode = 'like'; target = state.like; results = rankLike(target); }
      else if (state.mode === 'tags' && state.tags.size) { mode = 'tags'; results = rankTags(); }
      else results = rankBrowse();

      results = dedicatedFirst(results);
      const { main, family } = shelve(results, target);
      const bits = filterBits();
      const fText = bits.length ? bits.join(' · ') : 'all games';
      const head = $('headline'), sub = $('sub');

      if (mode === 'like') {
        const strong = main.slice(0, cfg.count).some(r => r.conf >= 0.2);
        head.textContent = main.length ? (strong ? `If you like ${target.name}, try these` : `The closest we have to ${target.name}`) : `Nothing close to ${target.name} right now`;
        sub.textContent = main.length ? `Matched on how it plays and its theme · ${fText}` : 'Try removing a filter or turning off "In stock only".';
      } else if (mode === 'tags') {
        const picked = [...state.tags];
        const list = picked.length > 1 ? picked.slice(0, -1).join(', ') + ' and ' + picked[picked.length - 1] : picked[0];
        head.textContent = main.length ? `Games for fans of ${list.toLowerCase()}` : 'No games tick those boxes';
        sub.textContent = main.length ? `${main.length} match at least one · most boxes ticked first · ${fText}` : 'Pick fewer options or remove a filter';
      } else {
        // "Popular on our shelves" only before any choice; after that, "You may enjoy these"
        const anyChoice = state.players !== 'any' || state.time !== 'any' || state.weight !== 'any';
        head.textContent = anyChoice ? 'You may enjoy these' : 'Popular on our shelves';
        const order = state.players === '1' ? 'solo-only games first, then by BGG rank'
          : state.players === '2' ? 'two-player-only games first, then by BGG rank'
          : 'sorted by BGG rank';
        sub.textContent = `${main.length} games · ${fText} · ${order}`;
      }

      const grid = $('grid');
      const famWrap = $('family');
      if (!main.length) {
        grid.innerHTML = '<li class="gf-empty">No matches with these filters</li>';
        famWrap.hidden = true;
      } else {
        grid.innerHTML = skeleton(Math.min(cfg.count, main.length));
        const cards = await cardsFor(main, cfg.count);
        if (token !== renderToken) return;
        grid.innerHTML = cards.map(itemHtml).join('') || '<li class="gf-empty">No matches with these filters</li>';
        afterInsert(grid);

        if (mode === 'like' && family.length && !cfg.compact) {
          const famCards = await cardsFor(family, 4);
          if (token !== renderToken) return;
          if (famCards.length) {
            $('family-title').textContent = `More from the ${franchiseName(franchiseKeys(target)[0])} family`;
            $('family-grid').innerHTML = famCards.map(c => itemHtml({ r: { reasons: [], g: c.r.g }, html: c.html })).join('');
            famWrap.hidden = false;
            afterInsert($('family-grid'));
          } else famWrap.hidden = true;
        } else famWrap.hidden = true;
      }

      const params = toParams().toString();
      const more = $('more');
      if (more) {
        more.hidden = !cfg.fullUrl;
        more.href = cfg.fullUrl + (params ? (cfg.fullUrl.includes('?') ? '&' : '?') + params : '');
      }
      if (!cfg.compact) {
        try { history.replaceState(null, '', location.pathname + (params ? '?' + params : '') + location.hash); } catch (e) {}
      }
    }

    // --- controls ---
    function chips(el, items, onPick, isOn, kind) {
      if (!el) return;
      const mod = kind ? ` gf-chip--${kind}` : '';
      el.innerHTML = items.map(it => {
        const bars = it.bars ? `<span class="gf-meter gf-meter--chip" aria-hidden="true">${barSegs(it.bars)}</span>` : '';
        return `<button type="button" class="gf-chip${mod}" data-v="${esc(it.id || it.label)}" aria-pressed="false">${esc(it.label)}${bars}</button>`;
      }).join('');
      el.addEventListener('click', e => { const b = e.target.closest('.gf-chip'); if (b) { onPick(b.dataset.v); sync(); render(); } });
      el._isOn = isOn;
    }
    function sync() {
      root.querySelectorAll('[data-role$="chips"]').forEach(el => {
        el.querySelectorAll('.gf-chip').forEach(b => b.setAttribute('aria-pressed', String(el._isOn(b.dataset.v))));
      });
      const likeTab = $('tab-like'), tagTab = $('tab-tags');
      if (likeTab) likeTab.setAttribute('aria-selected', String(state.mode === 'like'));
      if (tagTab) tagTab.setAttribute('aria-selected', String(state.mode === 'tags'));
      $('pane-like').hidden = state.mode !== 'like';
      if ($('pane-tags')) $('pane-tags').hidden = state.mode !== 'tags';
      $('search').hidden = !!state.like;
      $('picked').hidden = !state.like;
      $('try').hidden = !!state.like;
      if (state.like) $('picked-name').textContent = state.like.name;
      if ($('stock')) $('stock').checked = state.stock;
    }

    function wire() {
      chips($('players-chips'), PLAYERS, v => { state.players = v; }, v => v === state.players);
      chips($('time-chips'), TIMES, v => { state.time = v; }, v => v === state.time);
      chips($('weight-chips'), WEIGHTS, v => { state.weight = v; }, v => v === state.weight);
      const toggleTag = v => { state.tags.has(v) ? state.tags.delete(v) : state.tags.add(v); };
      chips($('mech-chips'), D.MECHS, toggleTag, v => state.tags.has(v), 'mechanism');
      chips($('theme-chips'), D.THEMES, toggleTag, v => state.tags.has(v), 'genre');

      const likeTab = $('tab-like'), tagTab = $('tab-tags');
      if (likeTab) likeTab.addEventListener('click', () => { state.mode = 'like'; sync(); render(); });
      if (tagTab) tagTab.addEventListener('click', () => { state.mode = 'tags'; sync(); render(); });
      if ($('stock')) $('stock').addEventListener('change', e => { state.stock = e.target.checked; render(); });
      if ($('reset')) $('reset').addEventListener('click', () => {
        Object.assign(state, { players: cfg.defaultPlayers, time: 'any', weight: 'any', like: null, stock: true, mode: 'like' });
        state.tags.clear(); $('q').value = ''; sync(); render();
      });
      if ($('jump')) $('jump').addEventListener('click', () => $('results').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' }));
      root.querySelector('form').addEventListener('submit', e => e.preventDefault());

      // "A game I love" search
      const q = $('q'), list = $('list');
      let active = -1, opts = [];
      const close = () => { list.hidden = true; q.setAttribute('aria-expanded', 'false'); active = -1; };
      const pick = g => { state.like = g; state.mode = 'like'; q.value = ''; close(); sync(); render(); };
      q.addEventListener('input', () => {
        const k = norm(q.value);
        if (!k) { close(); return; }
        const starts = s => s.key.startsWith(k) || s.alt.some(a => a.startsWith(k));
        opts = D.searchable.filter(s => s.key.includes(k) || s.alt.some(a => a.includes(k)))
          .sort((a, b) => (starts(a) ? 0 : 1) - (starts(b) ? 0 : 1) || (a.g.rank || 99999) - (b.g.rank || 99999))
          .slice(0, 8);
        list.innerHTML = opts.length
          ? opts.map((s, i) => { const ai = s.key.includes(k) ? -1 : s.alt.findIndex(a => a.includes(k)); const label = ai >= 0 ? `${s.g.name} (${s.altRaw[ai]})` : s.g.name; return `<button type="button" class="gf-opt" role="option" id="${cfg.sectionId}-opt${i}" aria-selected="false" data-i="${i}">${esc(label)}<span>${s.g.shop ? 'In our shop' : (s.g.year || '')}</span></button>`; }).join('')
          : '<div class="gf-opt" aria-disabled="true">No match. Try another game.</div>';
        list.hidden = false; q.setAttribute('aria-expanded', 'true'); active = -1;
      });
      q.addEventListener('keydown', e => {
        if (list.hidden || !opts.length) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          active = (active + (e.key === 'ArrowDown' ? 1 : -1) + opts.length) % opts.length;
          list.querySelectorAll('.gf-opt').forEach((o, i) => o.setAttribute('aria-selected', String(i === active)));
          q.setAttribute('aria-activedescendant', `${cfg.sectionId}-opt${active}`);
        } else if (e.key === 'Enter') { e.preventDefault(); pick(opts[Math.max(active, 0)].g); }
        else if (e.key === 'Escape') close();
      });
      list.addEventListener('click', e => { const b = e.target.closest('.gf-opt[data-i]'); if (b) pick(opts[+b.dataset.i].g); });
      document.addEventListener('click', e => { if (!$('search').contains(e.target)) close(); });
      $('clear').addEventListener('click', () => { state.like = null; sync(); render(); q.focus(); });

      const TRY = ['Catan', 'Ticket to Ride', 'Wingspan', 'Codenames', 'Azul'];
      $('try').innerHTML = 'Try: ' + TRY.map(n => D.ALL.find(g => g.name === n)).filter(Boolean)
        .map(g => `<button type="button" data-b="${g.bgg}">${esc(g.name)}</button>`).join('');
      $('try').addEventListener('click', e => { const b = e.target.closest('[data-b]'); if (b) pick(D.byBgg.get(+b.dataset.b)); });

      const foot = $('foot');
      if (foot) foot.textContent = `${D.SHOP.length} games matched. Game data from BoardGameGeek. Last updated ${new Date(D.INDEX.generated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}`;
    }

    loadIndex(cfg.indexUrl)
      .then(data => {
        D = data;
        root.classList.add('gf--ready');
        wire();
        fromParams();
        sync();
        render();
      })
      .catch(err => {
        console.error('[game-finder]', err);
        $('headline').textContent = 'The game finder is taking a break';
        $('sub').textContent = 'Please try again in a few minutes, or browse our board games';
        $('grid').innerHTML = '';
      });
  }

  function boot() { document.querySelectorAll('.gf[data-gf]:not([data-gf-init])').forEach(el => { el.dataset.gfInit = '1'; Finder(el); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  // Theme editor: re-run when the section is added or re-rendered
  document.addEventListener('shopify:section:load', boot);
})();